import { useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useDisconnectPartnerAccount } from '@/api/partner-accounts';
import type { PartnerAccount } from '@/types/api';

interface DisconnectAccountDialogProps {
  account: PartnerAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type HandlePublishedOption = 'unpublish_all' | 'keep_published';

export function DisconnectAccountDialog({
  account,
  open,
  onOpenChange,
}: DisconnectAccountDialogProps): JSX.Element {
  const { toast } = useToast();
  const disconnectAccount = useDisconnectPartnerAccount();
  const [handlePublished, setHandlePublished] = useState<HandlePublishedOption>('unpublish_all');

  const handleDisconnect = async () => {
    try {
      await disconnectAccount.mutateAsync({
        id: account.id,
        options: { handlePublishedLocations: handlePublished },
      });

      toast({
        title: 'Account Disconnected',
        description: `${account.name} has been disconnected successfully.`,
      });

      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to disconnect account';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Disconnect Account
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to disconnect <strong>{account.name}</strong>? This action cannot
            be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-2">
            <Label htmlFor="handle-published">What should happen to published locations?</Label>
            <Select
              value={handlePublished}
              onValueChange={(value) => { setHandlePublished(value as HandlePublishedOption); }}
            >
              <SelectTrigger id="handle-published" data-testid="handle-published-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unpublish_all">
                  Unpublish all locations from ScoutAround
                </SelectItem>
                <SelectItem value="keep_published">
                  Keep locations published (will no longer sync)
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {handlePublished === 'unpublish_all'
                ? 'All locations from this account will be removed from ScoutAround.'
                : 'Locations will remain on ScoutAround but won\'t receive updates from the partner platform.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => { onOpenChange(false); }}
            disabled={disconnectAccount.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDisconnect}
            disabled={disconnectAccount.isPending}
            data-testid="confirm-disconnect-btn"
          >
            {disconnectAccount.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Disconnect Account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
